import React, { useState, lazy, Suspense } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Badge, Drawer, Tooltip, Spin } from 'antd';
import { MailOutlined, BellOutlined } from '@ant-design/icons';
import { useLocale, Locale } from '../contexts/LocaleContext';
import { useUnreadCount, useNotifyUnreadCount } from '../pages/Inbox/handlers';
import { useUnreadAnnouncementCount } from '../pages/SystemAnnouncements/handlers';

const InboxPage = lazy(() => import('../pages/Inbox'));
const AnnouncementDrawer = lazy(() => import('../pages/SystemAnnouncements/AnnouncementDrawer'));

const Header: React.FC = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [showDropdown, setShowDropdown] = useState(false);
  const [showInbox, setShowInbox] = useState(false);
  const [showAnnouncements, setShowAnnouncements] = useState(false);
  const unreadCount = useUnreadCount();
  const { count: notifyUnreadCount, refresh: refreshNotifyCount } = useNotifyUnreadCount();
  const totalUnreadCount = unreadCount + notifyUnreadCount;
  const unreadAnnouncementCount = useUnreadAnnouncementCount();
  const { locale, setLocale } = useLocale();

  const handleLogout = async () => {
    await logout();
  };

  return (
    <header className="fixed top-0 left-0 right-0 bg-white shadow-md z-40">
      <div className="px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo and Title */}
          <div className="flex items-center ml-2 cursor-pointer" onClick={() => navigate('/')}>
            <img src="/logo.png" alt="e.dye" style={{ height: 44 }} />
          </div>

          {/* User Menu */}
          <div className="flex items-center space-x-4">
            {/* Locale Toggle */}
            <Tooltip title={locale === 'zh_TW' ? '切換簡體' : '切换繁体'}>
              <div className="flex items-center bg-gray-100 rounded-full p-1 select-none">
                <button
                  onClick={() => setLocale('zh_TW')}
                  className={`px-3 py-1 rounded-full text-sm transition-all duration-200 ${
                    locale === 'zh_TW'
                      ? 'bg-white text-blue-600 shadow-sm font-semibold'
                      : 'text-gray-400 hover:text-gray-600'
                  }`}
                >
                  繁
                </button>
                <button
                  onClick={() => setLocale('zh_CN')}
                  className={`px-3 py-1 rounded-full text-sm transition-all duration-200 ${
                    locale === 'zh_CN'
                      ? 'bg-white text-blue-600 shadow-sm font-semibold'
                      : 'text-gray-400 hover:text-gray-600'
                  }`}
                >
                  简
                </button>
              </div>
            </Tooltip>

            {/* Inbox Icon */}
            <button
              onClick={() => { setShowInbox(true); refreshNotifyCount(); }}
              className="relative text-gray-500 hover:text-blue-500 transition-colors"
              title="收件夾"
            >
              <Badge count={totalUnreadCount} size="small" offset={[2, -2]}>
                <MailOutlined style={{ fontSize: 20 }} />
              </Badge>
            </button>

            {/* Announcement Bell Icon */}
            <button
              onClick={() => setShowAnnouncements(true)}
              className="relative text-gray-500 hover:text-orange-500 transition-colors"
              title="系統公告"
            >
              <Badge count={unreadAnnouncementCount} size="small" offset={[2, -2]}>
                <BellOutlined style={{ fontSize: 20 }} />
              </Badge>
            </button>

            {/* User Dropdown */}
            <div className="relative">
              <button
                onClick={() => setShowDropdown(!showDropdown)}
                className="flex items-center space-x-3 text-sm font-medium text-gray-700 hover:text-gray-900"
              >
                <div className="flex items-center space-x-2">
                  <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center text-white">
                    {user?.userName?.charAt(0) || 'U'}
                  </div>
                  <div className="text-left">
                    <div className="font-medium">{user?.userName || '使用者'}</div>
                    <div className="text-xs text-gray-500">{[user?.department?.dptName || user?.deptName, user?.titleCode || user?.positionName].filter(Boolean).join(' · ') || '—'}</div>
                  </div>
                </div>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                </svg>
              </button>

              {/* Dropdown Menu */}
              {showDropdown && (
                <>
                  <div
                    className="fixed inset-0 z-10"
                    onClick={() => setShowDropdown(false)}
                  ></div>
                  <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg py-1 z-20">
                    <div className="px-4 py-2 text-sm text-gray-700 border-b">
                      <div className="font-medium">{user?.userName}</div>
                      <div className="text-xs text-gray-500">{user?.email}</div>
                    </div>

                    <button
                      onClick={() => {
                        setShowDropdown(false);
                        navigate('/user/change-password');
                      }}
                      className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                    >
                      <div className="flex items-center">
                        <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
                        </svg>
                        修改密碼
                      </div>
                    </button>

                    <div className="border-t"></div>

                    <button
                      onClick={handleLogout}
                      className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                    >
                      <div className="flex items-center">
                        <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                        </svg>
                        登出
                      </div>
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
      <Drawer
        title={null}
        placement="right"
        open={showInbox}
        onClose={() => setShowInbox(false)}
        width={720}
        styles={{ body: { padding: 0 } }}
        destroyOnClose={false}
      >
        <Suspense fallback={<div className="flex justify-center py-12"><Spin size="large" /></div>}>
          <InboxPage onClose={() => setShowInbox(false)} />
        </Suspense>
      </Drawer>
      <Drawer
        title="系統公告"
        placement="right"
        open={showAnnouncements}
        onClose={() => setShowAnnouncements(false)}
        width={560}
        styles={{ body: { padding: 0 } }}
        destroyOnClose
      >
        <Suspense fallback={<div className="flex justify-center py-12"><Spin size="large" /></div>}>
          <AnnouncementDrawer />
        </Suspense>
      </Drawer>
    </header>
  );
};

export default Header;
